import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './HelpDrawer.css';

interface HelpDrawerProps {
  helpKey: string;
  isOpen: boolean;
  onClose: () => void;
}

const HelpDrawer: React.FC<HelpDrawerProps> = ({ helpKey, isOpen, onClose }) => {
  const { t } = useTranslation();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      // Move focus to close button when drawer opens
      setTimeout(() => closeButtonRef.current?.focus(), 0);
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
      // Restore focus to previously focused element
      previousFocusRef.current?.focus();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Check if the help key has content
  const title = t(`${helpKey}.title`, { defaultValue: '' });
  const overview = t(`${helpKey}.overview`, { defaultValue: '' });
  const workflow = t(`${helpKey}.workflow`, { defaultValue: '' });
  const concepts = t(`${helpKey}.concepts`, { defaultValue: '' });
  const tips = t(`${helpKey}.tips`, { defaultValue: '' });

  // If no content at all, don't render
  if (!title && !overview) return null;

  return createPortal(
    <>
      <div className="help-drawer-overlay" onClick={onClose} />
      <aside className="help-drawer" aria-label={t('helpButton.ariaLabel')}>
        <div className="help-drawer__header">
          <h3 className="help-drawer__title">{title}</h3>
          <button
            ref={closeButtonRef}
            className="help-drawer__close"
            onClick={onClose}
            aria-label={t('helpButton.closeAriaLabel')}
          >
            &times;
          </button>
        </div>
        <div className="help-drawer__content">
          {overview && (
            <section className="help-drawer__section">
              {overview.split('\n\n').map((paragraph, i) => (
                <p key={i}>{paragraph}</p>
              ))}
            </section>
          )}

          {workflow && (
            <section className="help-drawer__section">
              <h4>{t('help.workflowTitle', { defaultValue: 'How it works' })}</h4>
              <div className="help-drawer__workflow">
                {workflow.split('\n').filter(line => line.trim()).map((step, i) => (
                  <p key={i}>{step.replace(/^\d+\.\s*/, '')}</p>
                ))}
              </div>
            </section>
          )}

          {concepts && (
            <section className="help-drawer__section">
              <h4>{t('help.conceptsTitle', { defaultValue: 'Key concepts' })}</h4>
              <div className="help-drawer__concepts">
                {concepts.split('\n').filter(line => line.trim()).map((concept, i) => {
                  // Parse **bold** markdown
                  const parts = concept.replace(/^[•-]\s*/, '').split(/(\*\*[^*]+\*\*)/);
                  return (
                    <p key={i}>
                      {parts.map((part, j) => {
                        if (part.startsWith('**') && part.endsWith('**')) {
                          return <strong key={j}>{part.slice(2, -2)}</strong>;
                        }
                        return <span key={j}>{part}</span>;
                      })}
                    </p>
                  );
                })}
              </div>
            </section>
          )}

          {tips && (
            <section className="help-drawer__section">
              <h4>{t('help.tipsTitle', { defaultValue: 'Tips' })}</h4>
              <div className="help-drawer__tips">
                {tips.split('\n').filter(line => line.trim()).map((tip, i) => (
                  <p key={i}>{tip.replace(/^[•-]\s*/, '• ')}</p>
                ))}
              </div>
            </section>
          )}
        </div>
      </aside>
    </>,
    document.body
  );
};

export default HelpDrawer;
